import { Data, GenericAccountId } from '@polkadot/types';
import { RegistrationJudgement } from '@polkadot/types/interfaces';
import { PalletIdentityRegistration } from '@polkadot/types/lookup';
import { u8aToString } from '@polkadot/util';
import { useApi } from 'hooks/useApi';
import { useApiCall } from 'hooks/useApiCall';

export type CandidateIdentity = IdentityMetadata | undefined;

interface IdentityMetadata {
  display?: string;
  isVerified?: boolean;
  isLowQuality?: boolean;
  email?: string;
  twitter?: string;
}

interface SuperOfIdentity {
  address: string;
  displayChild: string;
}

function isVerified(judgements: RegistrationJudgement[]): boolean {
  return judgements.some(
    ([, judgement]) => judgement.isKnownGood || judgement.isReasonable,
  );
}

function isLowQuality(judgements: RegistrationJudgement[]): boolean {
  return judgements.some(([, judgement]) => judgement.isLowQuality);
}

function transformIdentity(
  data: PalletIdentityRegistration[],
): CandidateIdentity[] | undefined {
  return data.map((ident) => {
    const identity = Array.isArray(ident) ? ident[0] : ident;

    return {
      display: u8aToString(identity.info.display.asRaw.toU8a(true)),
      email: u8aToString(identity.info.email.asRaw.toU8a(true)) || undefined,
      twitter:
        u8aToString(identity.info.twitter.asRaw.toU8a(true)) || undefined,
      isVerified: isVerified(identity.judgements),
      isLowQuality: isLowQuality(identity.judgements),
    };
  });
}

function transformSuper(
  data: [GenericAccountId, Data][],
): (SuperOfIdentity | undefined)[] | undefined {
  return data.map((value) => ({
    address: value[0].toString(),
    displayChild: u8aToString(value[1].asRaw.toU8a(true)) as string,
  }));
}

export function useIdentities(
  candidates?: string[],
): CandidateIdentity[] | undefined {
  const api = useApi();

  const supers = useApiCall(
    api?.query.identity.superOf.multi,
    [candidates],
    transformSuper,
  );

  const parentsAddresses = supers?.map((parent) => parent?.address || '');
  const parentIdentities = useApiCall(
    api?.query.identity.identityOf.multi,
    [parentsAddresses],
    transformIdentity,
  );

  const identities = useApiCall(
    api?.query.identity.identityOf.multi,
    [candidates],
    transformIdentity,
  );

  return identities?.map((identity, i) => {
    const parent = parentIdentities?.[i];
    const displayParent = parent?.display;
    const displayOwn = identity?.display;
    const displayChild = supers?.[i]?.displayChild;

    return {
      display: displayParent ? `${displayParent} ${displayChild}` : displayOwn,
      isVerified: identity?.isVerified || parent?.isVerified,
      isLowQuality: identity?.isLowQuality || parent?.isLowQuality,
      email: identity?.email || parent?.email,
      twitter: identity?.twitter || parent?.twitter,
    };
  });
}

export function useIdentity(address?: string): CandidateIdentity {
  return useIdentities(address ? [address] : undefined)?.[0];
}
